import React from 'react';
import invariant from 'tiny-invariant';
import {
  AfterPageProps,
  createApiClient,
  createInitialPropsRedirectToLogin,
  makeAfterComponent,
  wrapLayout,
} from '../../@sprinx/react-after-razzle';
import { Branch } from '../../api/articles/Branch';
import { getBranchDetail } from '../../api/articles/branchList';
import commonThingsToLoad from '../../api/commonThingsToLoad';
import ErrorInternal from '../../components/ErrorInternal';
import BranchDetailPageLayout from './BranchDetailPageLayout';

export interface BranchDetailPagePageProps extends AfterPageProps {
  branchDetail?: Branch;
}

const BranchDetailPagePage: React.FC<BranchDetailPagePageProps> = ({ branchDetail, error }) => {
  if (error) return <ErrorInternal error={error} />;

  invariant(branchDetail, "BranchDetailPagePage requires 'branchDetail' to be defined.");

  return <BranchDetailPageLayout branchDetail={branchDetail} />;
};

BranchDetailPagePage.displayName = 'BranchDetailPagePage';

export default makeAfterComponent(wrapLayout(BranchDetailPagePage), async ({ match, initialAppProps, getAppProps }) => {
  try {
    const appProps = getAppProps ? getAppProps() : initialAppProps;
    const apiClient = createApiClient({ accessToken: appProps.auth?.accessToken });
    const locale = appProps.locale;
    const branchId = decodeURIComponent(match.params.id);

    if (!branchId) {
      throw new Error('Invalid branch id');
    }

    if (appProps) {
      const [branchDetail, ...commonThings] = await Promise.all([
        getBranchDetail(apiClient, locale, branchId),
        ...commonThingsToLoad.promises({ apiClient }),
      ]);

      appProps.propagateToState({
        ...commonThingsToLoad.toRegister(commonThings),
      });

      return { branchDetail };
    }

    const branchDetail = await getBranchDetail(apiClient, locale, branchId);
    return { branchDetail };
  } catch (error) {
    if (error.response?.status === 401) {
      return createInitialPropsRedirectToLogin({ match });
    }
    return { error };
  }
});
